import React, { useState, useEffect, useCallback } from 'react';
import { useConfirm } from '../common/ConfirmProvider';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import api from '../../services/api';

const Container = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: transparent;
  border-radius: 8px;
  border-left: none;
`;

// Title styled component removed - not used in component

const Description = styled.p`
  margin: 0 0 16px 0;
  font-size: 0.875rem;
  color: #000;
  line-height: 1.5;
`;

const FormSection = styled.div`
  background: white;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  min-height: 40px;

  &:focus {
    outline: none;
    border-color: #ff9800;
    box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--edit-radius-input, 10px);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 40px;
`;

const AddButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--edit-bg-card, #fff);
  color: var(--edit-text-muted, #6c757d);
  border: 1px solid var(--edit-border, rgba(0, 0, 0, 0.08));
  box-shadow: none;
  line-height: 1.4;
  min-height: 40px;

  &:hover:not(:disabled) {
    background: var(--background-secondary, #f7f5f1);
    color: var(--edit-text, #1a1a2e);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const AddTriggerButton = styled(AddButton)`
  margin-bottom: 12px;
`;

const CancelButton = styled(Button)`
  background: var(--edit-bg-card, #fff);
  color: var(--edit-text-muted, #6c757d);
  border: 1px solid var(--edit-border, rgba(0, 0, 0, 0.08));

  &:hover:not(:disabled) {
    background: var(--background-secondary, #f7f5f1);
    color: var(--edit-text, #1a1a2e);
  }
`;

const BreaksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BreakItem = styled.div`
  background: white;
  padding: 12px;
  border-radius: 6px;
  border-left: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const BreakInfo = styled.div`
  flex: 1;
`;

const BreakDates = styled.div`
  font-size: 13px;
  color: #333;
  font-weight: 500;
  margin-bottom: 4px;
`;

const BreakDescription = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;

const BreakDuration = styled.div`
  font-size: 12px;
  color: #999;
`;

const BreakActions = styled.div`
  display: flex;
  gap: 8px;
`;

const DeleteButton = styled(Button)`
  background-color: #f44336;
  color: white;
  padding: 6px 12px;
  font-size: 12px;

  &:hover {
    background-color: #d32f2f;
  }
`;

const EditButton = styled(Button)`
  background: #eef2ff;
  color: #3730a3;
  border: 1px solid #c7d2fe;
  padding: 6px 12px;
  font-size: 12px;

  &:hover {
    background: #e0e7ff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: #999;
  font-size: 13px;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
`;

const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
`;

/**
 * BreakPeriodsManager Component
 * Allows instructors to manage break periods for weekly submission assignments
 */
function BreakPeriodsManager({ assignmentId, isWeekly }) {
  const confirm = useConfirm();
  const [breakPeriods, setBreakPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBreakId, setEditingBreakId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    break_start_date: '',
    break_end_date: '',
    description: ''
  });

  // Fetch break periods on mount
  const fetchBreakPeriods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getBreakPeriods(assignmentId);
      if (response.success) {
        setBreakPeriods(response.breakPeriods || []);
        setError(null);
      } else {
        setBreakPeriods([]);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching break periods:', err);
      setError(err?.response?.message || err?.message || 'Failed to load break periods');
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    if (assignmentId && isWeekly) {
      fetchBreakPeriods();
    }
  }, [assignmentId, isWeekly, fetchBreakPeriods]);

  // Only show for weekly assignments
  if (!isWeekly) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const toLocalDateTimeInputValue = (isoString) => {
    if (!isoString) return '';
    try {
      return DateTime.fromISO(isoString).setZone('America/Chicago').toFormat("yyyy-LL-dd'T'HH:mm");
    } catch {
      return '';
    }
  };

  const resetFormState = () => {
    setFormData({
      break_start_date: '',
      break_end_date: '',
      description: ''
    });
    setEditingBreakId(null);
    setShowForm(false);
  };

  const handleStartAdd = () => {
    setError(null);
    setSuccess(null);
    setEditingBreakId(null);
    setFormData({
      break_start_date: '',
      break_end_date: '',
      description: ''
    });
    setShowForm(true);
  };

  const handleStartEdit = (breakPeriod) => {
    setError(null);
    setSuccess(null);
    setEditingBreakId(breakPeriod.id);
    setFormData({
      break_start_date: toLocalDateTimeInputValue(breakPeriod.break_start_date),
      break_end_date: toLocalDateTimeInputValue(breakPeriod.break_end_date),
      description: breakPeriod.description || ''
    });
    setShowForm(true);
  };

  const handleSaveBreak = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setError(null);
    setSuccess(null);

    // Validate form
    if (!formData.break_start_date || !formData.break_end_date) {
      setError('Please enter both start and end dates');
      return;
    }

    const startDate = new Date(formData.break_start_date);
    const endDate = new Date(formData.break_end_date);

    if (endDate <= startDate) {
      setError('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        break_start_date: startDate.toISOString(),
        break_end_date: endDate.toISOString(),
        description: formData.description || null
      };

      const response = editingBreakId
        ? await api.updateBreakPeriod(assignmentId, editingBreakId, payload)
        : await api.createBreakPeriod(assignmentId, payload);

      if (response.success) {
        setSuccess(editingBreakId ? 'Break period updated successfully' : 'Break period added successfully');
        resetFormState();
        await fetchBreakPeriods();
      } else {
        setError(response.message || (editingBreakId ? 'Failed to update break period' : 'Failed to add break period'));
      }
    } catch (err) {
      console.error('Error saving break period:', err);
      setError(err?.response?.message || err?.message || (editingBreakId ? 'Failed to update break period' : 'Failed to add break period'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBreak = async (breakId) => {
    if (!(await confirm({ title: 'Delete break period?', message: 'This break period will be removed.', confirmText: 'Delete', danger: true }))) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.deleteBreakPeriod(assignmentId, breakId);

      if (response.success) {
        setSuccess('Break period deleted successfully');
        if (editingBreakId === breakId) {
          resetFormState();
        }
        await fetchBreakPeriods();
      } else {
        setError(response.message || 'Failed to delete break period');
      }
    } catch (err) {
      console.error('Error deleting break period:', err);
      setError(err?.response?.message || err?.message || 'Failed to delete break period');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    try {
      const dt = DateTime.fromISO(dateString).setZone('America/Chicago');
      return dt.toFormat('MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const calculateDuration = (startDate, endDate) => {
    try {
      const start = DateTime.fromISO(startDate);
      const end = DateTime.fromISO(endDate);
      const diff = end.diff(start, ['days', 'hours']);
      const days = Math.floor(diff.days);
      const hours = Math.floor(diff.hours);

      if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } catch {
      return 'Unknown duration';
    }
  };

  return (
    <Container>
      <Description>
        Define scheduled breaks to skip weekly assignments. The schedule pauses during the selected dates and resumes afterward.
      </Description>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {!showForm ? (
        <AddTriggerButton type="button" onClick={handleStartAdd} disabled={loading}>
          + Add Scheduled Break
        </AddTriggerButton>
      ) : (
        <FormSection>
          <div>
            <FormRow>
              <FormGroup>
                <Label>Start Date & Time</Label>
                <Input
                  type="datetime-local"
                  name="break_start_date"
                  value={formData.break_start_date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>End Date & Time</Label>
                <Input
                  type="datetime-local"
                  name="break_end_date"
                  value={formData.break_end_date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Description (Optional)</Label>
                <Input
                  type="text"
                  name="description"
                  placeholder="e.g., Ramadan"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <div style={{ display: 'flex', gap: '8px' }}>
                <AddButton type="button" onClick={handleSaveBreak} disabled={loading}>
                  {loading ? (editingBreakId ? 'Saving...' : 'Adding...') : (editingBreakId ? 'Save' : 'Add')}
                </AddButton>
                <CancelButton
                  type="button"
                  onClick={resetFormState}
                  disabled={loading}
                >
                  Cancel
                </CancelButton>
              </div>
            </FormRow>
          </div>
        </FormSection>
      )}

      {breakPeriods.length > 0 ? (
        <BreaksList>
          {breakPeriods.map(breakPeriod => (
            <BreakItem key={breakPeriod.id}>
              <BreakInfo>
                <BreakDates>
                  {formatDateForDisplay(breakPeriod.break_start_date)} →{' '}
                  {formatDateForDisplay(breakPeriod.break_end_date)}
                </BreakDates>
                {breakPeriod.description && (
                  <BreakDescription>{breakPeriod.description}</BreakDescription>
                )}
                <BreakDuration>
                  Duration: {calculateDuration(breakPeriod.break_start_date, breakPeriod.break_end_date)}
                </BreakDuration>
              </BreakInfo>
              <BreakActions>
                <EditButton
                  type="button"
                  onClick={() => handleStartEdit(breakPeriod)}
                  disabled={loading}
                >
                  Edit
                </EditButton>
                <DeleteButton
                  type="button"
                  onClick={() => handleDeleteBreak(breakPeriod.id)}
                  disabled={loading}
                >
                  Delete
                </DeleteButton>
              </BreakActions>
            </BreakItem>
          ))}
        </BreaksList>
      ) : (
        !showForm && !error && <EmptyState>No scheduled breaks defined</EmptyState>
      )}
    </Container>
  );
}

export default BreakPeriodsManager;
